import { Dispatch, useCallback, useEffect, useState } from "react";
import { Button, Checkbox, DropZone, Icon, Thumbnail } from "@shopify/polaris";
import { DeleteIcon, PlusIcon } from "@shopify/polaris-icons";
import { useDrop, useDrag, DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import styles from "./MediaGallery.module.css";
import config from "../../config";
import { postData } from "../../utilities/networkCalls";
import {
    Action,
    ImageItem,
    StandardConfigInterface,
    BxgyConfigInterface,
    MixMatchSectionalConfigInterface,
    BundleBuilderConfigInterface,
} from "../../apps/Bundles/Types";
import { Field } from "@shopify/react-form";
import CustomModal from "../CustomModal/CustomModal";
import { truncateFileName } from "../../utilities/functions";
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_FILE_TYPES = ["image/jpeg", "image/png", "image/gif"];
interface SingleImageItemProps {
    file: ImageItem;
    index: number;
    handleDelete: (index: number) => void;
    moveImage: (dragIndex: number, hoverIndex: number) => void;
}

interface MediaGalleryProps {
    images: Field<ImageItem[]>;
    dispatchBundleConfig:
        | Dispatch<Action<StandardConfigInterface>>
        | Dispatch<Action<BxgyConfigInterface>>
        | Dispatch<Action<MixMatchSectionalConfigInterface>>
        | Dispatch<Action<BundleBuilderConfigInterface>>;
    products?: any;
    updateKey?: string;
    parentKey?: string;
    maxFiles?: number;
}

// Extracted ImageItem Component
const SingleImageItem = ({ file, index, handleDelete, moveImage }: SingleImageItemProps) => {
    const [{ isDragging }, ref] = useDrag({
        type: "IMAGE",
        item: { index },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const [, drop] = useDrop({
        accept: "IMAGE",
        drop: (draggedItem: { index: number }) => {
            if (draggedItem.index !== index) {
                moveImage(draggedItem.index, index);
            }
        },
    });

    return (
        <div
            ref={(node) => ref(drop(node))}
            className={`${styles.item} ${index === 0 ? styles.largeItem : styles.smallItem} ${
                isDragging ? styles.dragging : ""
            }`}
        >
            <span className={styles.imageNumber}>{index + 1}</span>

            {file.originalSource ? (
                <img src={file.originalSource} alt={file.alt} className={styles.image} />
            ) : (
                <div className={styles.uploadingPlaceholder}>Uploading...</div>
            )}
            <button className={styles.deleteButton} onClick={() => handleDelete(index)}>
                <Icon source={DeleteIcon} />
            </button>
        </div>
    );
};

const MediaGallery = ({
    images,
    dispatchBundleConfig,
    products,
    updateKey = "images",
    parentKey = "bundle_as_product",
    maxFiles = 10,
}: MediaGalleryProps) => {
    const [files, setFiles] = useState(images.value || []);
    const [includedProductImages, setIncludedProductImages] = useState<File[] | []>([]);
    const [selectedImages, setSelectedImages] = useState<File[] | []>([]);
    const [openModal, setOpenModal] = useState(false);
    console._log("images", images);

    useEffect(() => {
        if (products?.value?.length) {
            const arr: any = [];
            products.value.map((product: any) => {
                product.images.map((ps: any) => {
                    arr.push(ps);
                });
            });
            setIncludedProductImages(arr);
        } else {
            setIncludedProductImages([]);
        }
        products?.value;
    }, [products]);
    // Utility function to truncate long file names

    const processFiles = async (
        newFiles: File[],
        currentFiles: ImageItem[],
        setFiles: React.Dispatch<React.SetStateAction<ImageItem[]>>,
    ) => {
        const remainingFileCount = maxFiles - currentFiles.length;
        if (newFiles.length > remainingFileCount) {
            shopify.toast.show(`You can only upload up to ${maxFiles} files.`, { isError: true });
            return [];
        }

        const validFiles: File[] = [];
        for (const file of newFiles) {
            if (file?.name) {
                const fileName = truncateFileName(file.name, 20);

                if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
                    shopify.toast.show(`${fileName} is not a supported file type.`, {
                        isError: true,
                    });
                    await new Promise((resolve) => setTimeout(resolve, 1000));
                } else if (file.size > MAX_FILE_SIZE) {
                    shopify.toast.show(`${fileName} exceeds the 5MB file size limit.`, { isError: true });
                    await new Promise((resolve) => setTimeout(resolve, 1000));
                } else {
                    validFiles.push(file);
                }
            }
        }

        const filesToProcess = validFiles.slice(0, remainingFileCount);

        for (const file of filesToProcess) {
            // Add a placeholder to show "Uploading..."
            const placeholderItem: ImageItem = {
                originalSource: "",
                alt: file.name,
                mediaContentType: "IMAGE",
            };

            setFiles((prevFiles) => [...prevFiles, placeholderItem]);

            const data = new FormData();
            data.append("file", file);

            try {
                const response = await postData({
                    url: `/${config.appName}/upload`,
                    payload: data,
                });

                if (!response || !response.data || !response.data.url) {
                    throw new Error("Invalid response format");
                }

                const originalSource = response.data.url;

                setFiles((prevFiles) => {
                    const updatedFiles = [...prevFiles];
                    // Replace the last added placeholder with the real file data
                    updatedFiles[updatedFiles.length - 1] = {
                        ...updatedFiles[updatedFiles.length - 1],
                        originalSource,
                    };
                    currentFiles = updatedFiles.filter((uf) => !!uf?.originalSource);
                    return currentFiles;
                });
            } catch (error) {
                console.error("Error during file upload:", error);
                setFiles((prevFiles) => prevFiles.slice(0, -1)); // Remove the placeholder if upload fails
                shopify.toast.show("Upload failed! Please try again.", {
                    isError: true,
                });
            }
        }

        return currentFiles; // Return the current files after processing (mostly for consistency)
    };

    const handleDropZoneDrop = async (_dropFiles: File[], acceptedFiles: File[]) => {
        const currentFiles = await processFiles(acceptedFiles, files, setFiles);
        dispatchBundleConfig({
            payload: {
                [updateKey]: currentFiles, // Files state is already updated inside processFiles
            },
            isComplex: true,
            key: [parentKey] as any,
        });

        images.onChange(currentFiles);
    };

    console._log("files", files);

    const handleDelete = (index: number) => {
        setFiles((prevFiles) => {
            const updatedFiles = [...prevFiles];
            updatedFiles.splice(index, 1);
            dispatchBundleConfig({
                payload: {
                    [updateKey]: updatedFiles,
                },
                isComplex: true,
                key: [parentKey] as any,
            });
            images.onChange(updatedFiles);
            return updatedFiles;
        });
    };

    const moveImage = (dragIndex: number, hoverIndex: number) => {
        setFiles((prevFiles) => {
            const reorderedFiles = [...prevFiles];
            const draggedItem = reorderedFiles[dragIndex];
            reorderedFiles[dragIndex] = reorderedFiles[hoverIndex];
            reorderedFiles[hoverIndex] = draggedItem;
            dispatchBundleConfig({
                payload: {
                    [updateKey]: reorderedFiles,
                },
                isComplex: true,

                key: [parentKey] as any,
            });
            images.onChange(reorderedFiles);

            return reorderedFiles;
        });
    };

    const onImageClicked = useCallback((img: any) => {
        setSelectedImages((prevSelectedImages) => {
            // Check if the image is already selected
            const isAlreadySelected = prevSelectedImages.some((i: any) => i.id === img.id);

            if (isAlreadySelected) {
                // Remove the image if it's already selected
                return prevSelectedImages.filter((i: any) => i.id !== img.id);
            } else {
                // Add the image to the selection
                return [...prevSelectedImages, img];
            }
        });
    }, []);

    return (
        <div className={styles.mediaGalleryWrapper}>
            <div className={styles.mediaHeadSection}>
                <label className={styles.mediaGalleryLabel}>Media</label>{" "}
                {products?.value?.length > 0 && (
                    <Button
                        onClick={() => {
                            setOpenModal(true);
                        }}
                        variant={"plain"}
                    >
                        Add Images from included products
                    </Button>
                )}
            </div>
            <DndProvider backend={HTML5Backend}>
                <div>
                    {/* Show the upper DropZone only if there are no files */}
                    {files.length === 0 ? (
                        <div className={styles.dropZoneContainer}>
                            <DropZone onDrop={handleDropZoneDrop}>
                                <div className={styles.dropZonePlaceholder}>
                                    <div className={styles.dropZoneContent}>Click to upload images</div>
                                    <p className={styles.additionalInfo}>
                                        Upload Images: Accepts .jpg, .png, and .gif files up to 5MB.
                                    </p>
                                </div>
                            </DropZone>
                        </div>
                    ) : (
                        <div className={styles.gridContainer}>
                            {files.map((file, index) => (
                                <SingleImageItem
                                    key={index}
                                    file={file}
                                    index={index}
                                    handleDelete={handleDelete}
                                    moveImage={moveImage}
                                />
                            ))}

                            {/* Show the lower DropZone only if files length is less than maxFiles */}
                            {files.length < maxFiles && (
                                <div className={styles.addImageItem}>
                                    <DropZone onDrop={handleDropZoneDrop}>
                                        <div className={styles.addImageItemContent}>
                                            <Icon source={PlusIcon} />
                                        </div>
                                    </DropZone>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </DndProvider>
            <CustomModal
                title={"Add media from included products"}
                onClose={() => setOpenModal(false)}
                open={openModal}
                children={
                    <div className={styles.includedProductImagesWrapper}>
                        {includedProductImages.length
                            ? includedProductImages.map((img: any) => {
                                  return (
                                      <div className={styles.includedImgBox}>
                                          <div
                                              className={styles.thumbnailWrapper}
                                              onClick={() => {
                                                  onImageClicked(img);
                                              }}
                                          >
                                              <Thumbnail
                                                  alt={img?.id}
                                                  source={img?.originalSrc}
                                                  key={img?.id}
                                                  size={"large"}
                                              />
                                          </div>
                                          <span className={styles.includedImgSelector}>
                                              <Checkbox
                                                  checked={!!selectedImages.find((i: any) => img?.id == i.id)}
                                                  onChange={() => {
                                                      onImageClicked(img);
                                                  }}
                                                  label={img?.id}
                                                  labelHidden
                                              />
                                          </span>
                                      </div>
                                  );
                              })
                            : "No Media Available"}
                    </div>
                }
                primaryAction={{
                    content: "Add",
                    disabled: !selectedImages.length,
                    onAction: () => {
                        setFiles((ps: any) => {
                            const arr = [
                                ...ps,
                                ...selectedImages.map((ps: any) => ({
                                    originalSource: ps.originalSrc,
                                    alt: ps.id,
                                    mediaContentType: "IMAGE",
                                })),
                            ];
                            dispatchBundleConfig({
                                payload: {
                                    [updateKey]: arr,
                                },
                                isComplex: true,
                                key: [parentKey] as any,
                            });
                            images.onChange(arr);
                            return arr;
                        });
                        setSelectedImages([]);
                        setOpenModal(false);
                    },
                }}
                secondaryActions={[
                    {
                        content: "Cancel",
                        onAction: () => {
                            setOpenModal(false);
                        },
                    },
                ]}
            />
        </div>
    );
};

export default MediaGallery;
